import { useState, useEffect, useCallback } from 'react';
import { Notification, NotificationStats } from '../types/notifications';
import {
  getNotifications,
  getNotificationStats,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../services/notifications';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar notificações
  const loadNotifications = useCallback(async (limit?: number, offset?: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getNotifications(limit, offset);
      setNotifications(data);
    } catch (err) {
      setError('Erro ao carregar notificações');
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar estatísticas
  const loadStats = useCallback(async () => {
    try {
      const data = await getNotificationStats();
      setStats(data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  }, []);

  // Marcar como lida
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => prev.map(n => (n.id === notificationId ? { ...n, read: true } : n)));
      // Atualizar estatísticas
      setStats(prev =>
        prev
          ? {
              ...prev,
              unread: Math.max(0, prev.unread - 1),
            }
          : null,
      );
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    }
  }, []);

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setStats(prev => (prev ? { ...prev, unread: 0 } : null));
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
    }
  }, []);

  // Atualizar automaticamente as estatísticas
  const refreshStats = useCallback(() => {
    loadStats();
  }, [loadStats]);

  // Carregar dados iniciais
  useEffect(() => {
    loadNotifications();
    loadStats();
  }, [loadNotifications, loadStats]);

  // Polling para atualizações (opcional)
  useEffect(() => {
    const interval = setInterval(() => {
      refreshStats();
    }, 30000); // Atualizar a cada 30 segundos

    return () => clearInterval(interval);
  }, [refreshStats]);

  return {
    notifications,
    stats,
    loading,
    error,
    loadNotifications,
    loadStats,
    markAsRead,
    markAllAsRead,
    refreshStats,
  };
};
